import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SubscriptionPanel from "./SubscriptionPanel";
import client from "../../api/client";
import { AuthContext } from "../../contextStore/AuthContext";
import "./Profile.css";
import RenewModal from "./RenewModal";
import formatToIST from "../../utils/formatToIST"; // <-- added
import { parseToUtcDate } from '../../utils/utcParse'; // add at top with other imports
import SellerPlansModal from "../Plans/SellerPlansModal"; // Adjust path as needed


// ---------- Helpers for displayed views (UI only) ----------
function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Compute a displayed views number for UI only and persist it per-user-per-machine.
 * - real <= 1 : show random 3..5
 * - real <= 5 : show real * (1..3)
 * - real > 5  : show real + rand(0 .. floor(real * 0.05))
 *
 * Stored key: display_views_<machineId>
 * Returns integer.
 */
function getDisplayedViewsFor(machineId, realViews) {
  const key = `display_views_${machineId}`;
  try {
    const stored = window.localStorage.getItem(key);
    if (stored !== null && stored !== undefined) {
      const n = Number(stored);
      if (!Number.isNaN(n)) return n;
    }

    const real = Number(realViews) || 0;
    let displayed = 0;

    if (real <= 1) {
      displayed = randInt(3, 5);
    } else if (real <= 5) {
      const mult = randInt(1, 3);
      displayed = real * mult;
    } else {
      const bumpMax = Math.max(1, Math.floor(real * 0.05));
      displayed = real + randInt(0, bumpMax);
    }

    displayed = Math.max(0, Math.floor(displayed));
    window.localStorage.setItem(key, String(displayed));
    return displayed;
  } catch (e) {
    return Number(realViews) || 0;
  }
}

/**
 * Invalidate persisted displayed value for a machine (used after optimistic increment)
 */
function invalidateDisplayedViewsCache(machineId) {
  try {
    const key = `display_views_${machineId}`;
    window.localStorage.removeItem(key);
  } catch (e) { /* ignore */ }
}
// ----------------------------------------------------------------


export default function ProfileComponent({ user = {}, listings = [], onLogout, onEdit }) {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext) || {};

  const roleIsSeller = user?.role && String(user.role).includes("seller");
  const userId = user?.id;

  // local listings state so we can update single listing without full page reload
  const [listingsState, setListingsState] = useState(listings || []);

  // modal state
  const [renewModalOpen, setRenewModalOpen] = useState(false);
  const [renewTargetId, setRenewTargetId] = useState(null);

  const [plansOpen, setPlansOpen] = useState(false);
  const [selectedMachineId, setSelectedMachineId] = useState(null);

  // local state for contact loading / publish / mark-sold / renew actions
  const [contactLoadingId, setContactLoadingId] = useState(null);
  const [publishLoadingId, setPublishLoadingId] = useState(null);
  const [markSoldLoadingId, setMarkSoldLoadingId] = useState(null);
  const [renewLoadingId, setRenewLoadingId] = useState(null);

  // new: loading id for record-view call
  const [viewLoadingId, setViewLoadingId] = useState(null);

  useEffect(() => {
    // if props change externally, sync them
    setListingsState(listings || []);
  }, [listings]);

  // Format rupees nicely
  const fmtRupee = (val) => {
    try {
      return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(val || 0));
    } catch {
      return `₹${val || 0}`;
    }
  };

  // Compute human readable days/hours/mins left from visible_until
  const daysLeftFromVisible = (visibleUntil) => {
    if (!visibleUntil) return null;
    const expires = parseToUtcDate(visibleUntil);
    if (!expires) return { seconds: 0, label: "Invalid date" };
    const now = new Date();
    const diffMs = expires.getTime() - now.getTime();
    if (diffMs <= 0) return { seconds: 0, label: "Expired" };

    const seconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(seconds / 3600);
    const days = Math.floor(hours / 24);

    if (days >= 1) {
      return { seconds, label: `${days} day${days > 1 ? "s" : ""} left` };
    }
    if (hours >= 1) {
      return { seconds, label: `${hours} hr${hours > 1 ? "s" : ""} left` };
    }
    const minutes = Math.ceil(seconds / 60);
    return { seconds, label: `${minutes} min${minutes > 1 ? "s" : ""} left` };
  };

  const getStatusLabelAndColor = (listing, visibleInfo = null, visibleUntil = null) => {
    const computedLabel = visibleInfo && visibleInfo.label ? visibleInfo.label : null;
    const remainingReadable = computedLabel || listing.remaining_readable || null;

    if (String(listing.status) === "sold") {
      return {
        label: "Sold",
        colorClass: "sold",
        hint: listing.sold_at ? `Sold on ${formatToIST(listing.sold_at)}` : "Sold"
      };
    }

    if (remainingReadable && remainingReadable === "Expired") {
      return { label: "Expired", colorClass: "expired", hint: "Visibility expired" };
    }

    if (remainingReadable && remainingReadable.toLowerCase().includes("day")) {
      const numMatch = (remainingReadable || "").match(/(\d+)/);
      const num = numMatch ? Number(numMatch[1]) : null;

      if (num !== null && num <= 3) {
        return {
          label: remainingReadable,
          colorClass: "expiring",
          hint: `Expires on ${visibleUntil ? formatToIST(visibleUntil) : "-"}`
        };
      }

      return {
        label: remainingReadable,
        colorClass: "published",
        hint: `Expires on ${visibleUntil ? formatToIST(visibleUntil) : "-"}`
      };
    }

    if (remainingReadable) {
      return { label: remainingReadable, colorClass: "published", hint: visibleUntil ? `Expires on ${formatToIST(visibleUntil)}` : null };
    }

    if (listing.is_paid) return { label: "Published", colorClass: "published", hint: listing.published_at ? `Published on ${formatToIST(listing.published_at)}` : null };

    return { label: "Draft", colorClass: "draft", hint: null };
  };

  // View contact handler (protected endpoint; server enforces access rules)
  const viewContact = async (listing) => {
    if (!listing || !listing.id) return;
    const id = listing.id;
    try {
      setContactLoadingId(id);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await client.get(`/api/machines/${id}/contact`, { headers });

      if (res && res.data && res.data.ok) {
        const body = res.data;
        const phone =
          (body.contact && (body.contact.phone || body.contact.phone_number)) ||
          body.phone ||
          "N/A";
        const sellerName =
          (body.contact && body.contact.seller_name) ||
          body.seller_name ||
          listing.seller_name ||
          "Seller";

        alert(`${sellerName}\nPhone: ${phone}`);
        return;
      }

      const msg = res && res.data && (res.data.message || res.data.error) ? (res.data.message || res.data.error) : "Could not fetch contact details.";
      alert(msg);
    } catch (err) {
      const status = err?.response?.status;
      const body = err?.response?.data || {};

      if (status === 402 || body?.error === "payment_required" || body?.error === "no_active_plan" || body?.error === "no_credits") {
        if (window.confirm("You need an active buyer plan to view contact details. Go to Plans?")) {
          navigate("/profile/plans");
        }
        return;
      }

      if (status === 401) {
        navigate(`/login?redirect=${encodeURIComponent(`/listing/${listing.id}`)}`);
        return;
      }

      const message = body?.message || err?.message || "Failed to fetch contact.";
      alert(message);
    } finally {
      setContactLoadingId(null);
    }
  };

  // NEW: handle view — record view on server, optimistic update, then navigate to listing page
  const handleView = async (listing) => {
    if (!listing || !listing.id) return;
    const id = listing.id;

    // optimistic UI increment of stored "real" views
    setListingsState(prev => prev.map(it => it.id === id ? { ...it, views: (Number(it.views) || 0) + 1 } : it));

    // invalidate displayed cache so user sees updated inflated value
    invalidateDisplayedViewsCache(id);

    try {
      setViewLoadingId(id);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Try primary path first, fallback to alternate path if it fails
      const endpoints = [`/machines/${id}/view`, `/api/machines/${id}/view`];

      let res = null;
      let lastErr = null;
      for (const ep of endpoints) {
        try {
          res = await client.post(ep, {}, { headers });
          if (res && res.data && res.data.ok) break; // success
        } catch (e) {
          lastErr = e;
          // try next
        }
      }

      // If backend returned updated machine, merge it into local state
      if (res && res.data && res.data.ok && res.data.machine) {
        const updated = res.data.machine;
        setListingsState(prev => prev.map(it => it.id === updated.id ? { ...it, ...updated } : it));
      } else {
        // no server sync — keep optimistic state (we already incremented)
        if (lastErr) {
          // if unauthorized, redirect to login to let the user sign in and view
          const status = lastErr?.response?.status;
          if (status === 401) {
            navigate(`/login?redirect=${encodeURIComponent(`/listing/${id}`)}`);
            return;
          }
          console.warn('record view errors (all endpoints attempted):', lastErr);
        }
      }
    } catch (err) {
      console.warn("Failed to record view:", err?.message || err);
    } finally {
      setViewLoadingId(null);
      // Always navigate to the listing page
      navigate(`/listing/${id}`);
    }
  };

  // Mark as sold (seller only)
  const markAsSold = async (listing) => {
    if (!listing || !listing.id) return;
    if (!window.confirm("Mark this listing as SOLD? This will hide it from public view. This action can be reverted by restore in seller profile.")) return;

    try {
      setMarkSoldLoadingId(listing.id);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await client.post(`/api/machines/${listing.id}/mark-sold`, {}, { headers });

      if (res && res.data && res.data.ok) {
        alert("Listing marked as sold.");
        // update local state
        setListingsState(prev => prev.map(it => it.id === listing.id ? { ...it, status: "sold", sold_at: new Date().toISOString() } : it));
        return;
      }

      alert(res?.data?.message || "Failed to mark as sold.");
    } catch (err) {
      const status = err?.response?.status;
      const body = err?.response?.data || {};

      if (status === 401) {
        navigate(`/login?redirect=${encodeURIComponent(`/edit-listing/${listing.id}`)}`);
        return;
      }

      alert(body?.message || err?.message || "Error while marking as sold.");
    } finally {
      setMarkSoldLoadingId(null);
    }
  };

  // Publish listing (existing)
  const publishListing = async (listingId) => {
    if (!listingId) return;
    if (!window.confirm("Publish this listing now? This will attempt to consume seller credit (or require payment).")) return;

    try {
      setPublishLoadingId(listingId);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await client.post(`/api/machines/${listingId}/publish`, {}, { headers });

      if (res && res.data && res.data.ok) {
        alert("Publish succeeded.");
        // update local listing if published
        const updatedMachine = (res.data.machine || null);
        if (updatedMachine) {
          setListingsState(prev => prev.map(it => it.id === updatedMachine.id ? updatedMachine : it));
        } else {
          window.location.reload();
        }
        return;
      }

      alert(res?.data?.message || "Failed to publish listing.");
    } catch (err) {
      const status = err?.response?.status;
      const body = err?.response?.data || {};

      if (status === 402 || body?.error === "no_credits" || body?.error === "payment_required") {
        if (window.confirm("No credits or plan available. Go buy a seller plan?")) {
          navigate("/profile/plans");
        }
        return;
      }

      if (status === 401) {
        navigate(`/login?redirect=${encodeURIComponent(`/edit-listing/${listingId}`)}`);
        return;
      }

      alert(body?.message || err?.message || "Error while publishing listing.");
    } finally {
      setPublishLoadingId(null);
    }
  };

  // Open renew modal for a listing
  const openRenewModal = (listing) => {
    if (!listing || !listing.id) return;
    setRenewTargetId(listing.id);
    setRenewModalOpen(true);
  };

  // Called when RenewModal reports success
  const handleRenewSuccess = (updatedMachine) => {
    if (!updatedMachine) {
      // fallback: reload
      window.location.reload();
      return;
    }
    // Ensure visible_until is normalized (backend should return ISO; just in case)
    if (updatedMachine.visible_until) {
      try {
        // if it's not an ISO already, convert to ISO
        const d = new Date(updatedMachine.visible_until);
        if (!isNaN(d.getTime())) updatedMachine.visible_until = d.toISOString();
      } catch (e) { /* ignore */ }
    }
    // Update local state
    setListingsState(prev => prev.map(it => it.id === updatedMachine.id ? { ...it, ...updatedMachine } : it));
    // Show a toast/alert with IST-formatted expiry
    alert("Listing renewed. New expiry: " + (updatedMachine.visible_until ? formatToIST(updatedMachine.visible_until) : "No expiry"));
  };

  // Old simple renew (kept for backward compatibility) - not used by new UI
  const renewListing = async (listing) => {
    if (!listing || !listing.id) return;
    if (!window.confirm("Renew this listing using an available credit? If you don't have credits you'll be redirected to Plans.")) return;

    try {
      setRenewLoadingId(listing.id);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await client.post(`/api/machines/${listing.id}/renew`, { type: "use_credit" }, { headers });

      if (res && res.data && res.data.ok && res.data.published) {
        alert("Listing renewed successfully.");
        // update local listing
        const updated = res.data.machine || null;
        if (updated) {
          setListingsState(prev => prev.map(it => it.id === updated.id ? updated : it));
        } else {
          window.location.reload();
        }
        return;
      }

      if (res && res.data && (res.data.needs_payment || res.data.requires_payment || res.data.needs_payment === true)) {
        if (window.confirm("No credits available. Go buy a seller plan?")) {
          navigate("/profile/plans");
        }
        return;
      }

      alert(res?.data?.message || "Could not renew listing.");
    } catch (err) {
      const status = err?.response?.status;
      const body = err?.response?.data || {};

      if (status === 402 || body?.error === "no_post_credits" || body?.needs_payment) {
        if (window.confirm("No credits available. Go buy a seller plan?")) {
          navigate("/profile/plans");
        }
        return;
      }

      if (status === 401) {
        navigate(`/login?redirect=${encodeURIComponent(`/edit-listing/${listing.id}`)}`);
        return;
      }

      alert(body?.message || err?.message || "Error while renewing listing.");
    } finally {
      setRenewLoadingId(null);
    }
  };

  return (
    <div className="om-profile-page">
      {/* Sidebar */}
      <aside className="om-profile-sidebar">
        <div className="om-profile-card">
          <div className="om-avatar-large">
            {user && user.avatar_url ? (
              <img src={`/uploads/${user.avatar_url}`} alt="avatar" className="om-avatar-img" />
            ) : (
              <div className="om-avatar-fallback">
                {user && user.name ? user.name.charAt(0).toUpperCase() : "U"}
              </div>
            )}
          </div>

          <h3 className="om-name">{user?.name || "User"}</h3>
          <p className="om-email">{user?.email || ""}</p>
        </div>


        <nav className="om-profile-nav">
          <button className="om-nav-item active" onClick={() => navigate("/profile")}>
            Profile
          </button>
          <button className="om-nav-item" onClick={() => navigate("/my-listings")}>
            My Listings
          </button>
          <button className="om-nav-item" onClick={() => navigate("/favorites")}>
            Favorites
          </button>
          <button className="om-nav-item" onClick={() => navigate("/messages")}>
            Messages
          </button>

          <button
            className="om-nav-item om-logout"
            onClick={() => {
              if (typeof onLogout === "function") onLogout();
            }}
          >
            Logout
          </button>
        </nav>
      </aside>

      {/* Main */}
      <main className="om-profile-main">
        {/* Subscription / Plan */}
        <SubscriptionPanel user={user} />

        {/* Seller listings */}
        {roleIsSeller && (
          <section className="om-listings" style={{ marginTop: 18 }}>
            <h2 className="om-section-title">My Listings</h2>

            {(!listingsState || listingsState.length === 0) ? (
              <div className="om-empty">You have no listings yet.</div>
            ) : (
              listingsState.map((l) => {
                const visibleUntil = l.visible_until || (l.seller_purchase && l.seller_purchase.expires_at) || l.expires_at || null;
                const visibleInfo = visibleUntil ? daysLeftFromVisible(visibleUntil) : null;

                const isExpired = (visibleInfo && visibleInfo.seconds === 0) || (l.remaining_readable === 'Expired');
                const isExpiringSoon = (visibleInfo && typeof visibleInfo.seconds === 'number') ? (visibleInfo.seconds <= 3 * 24 * 3600 && visibleInfo.seconds > 0) : false;

                const statusObj = getStatusLabelAndColor(l, visibleInfo, visibleUntil);

                return (
                  <div className="om-listing-row" key={l.id}>
                    <div className="om-listing-left">
                      <h4 className="om-listing-title">{l.title}</h4>
                      <div className="om-listing-meta">
                        <span>{fmtRupee(l.price)}</span>
                        <span> · {l.currency || "INR"}</span>
                        <span> · {getDisplayedViewsFor(l.id ?? null, l.views ?? 0)} views</span>
                        {typeof l.contact_views_stored !== "undefined" && (
                          <span> · {l.contact_views_stored} contact views</span>
                        )}
                      </div>
                      <div className="om-listing-dates">
                        <small>Created: {l.created_at ? formatToIST(l.created_at) : "-"}</small>
                        {visibleUntil && (
                          <small style={{ marginLeft: 10 }}> • Expires: {formatToIST(visibleUntil)}</small>
                        )}
                      </div>

                      <div style={{ marginTop: 8 }}>
                        <span className={`om-status ${statusObj.colorClass}`} title={statusObj.hint || ''}>
                          {statusObj.label}
                        </span>
                        {visibleInfo && visibleInfo.label && (
                          <small style={{ marginLeft: 8, color: "#666" }}>
                            {visibleInfo.label}
                          </small>
                        )}
                        {!visibleInfo && l.remaining_readable && typeof l.remaining_readable === "string" && (
                          <small style={{ marginLeft: 8, color: "#666" }}>
                            {l.remaining_readable}
                          </small>
                        )}
                      </div>
                    </div>

                    <div className="om-listing-right">
                      {l.is_paid ? (
                        <span className="om-status published">Published</span>
                      ) : (
                        <>
                          <button
                            className="om-btn secondary"
                            onClick={() => {
                              // Open the SellerPlansModal with the machineId
                              // You'll need to pass setPlansOpen and selectedMachineId to the component
                              setSelectedMachineId(l.id);
                              setPlansOpen(true);
                            }}
                          >
                            Pay & Publish
                          </button>

                          {userId && Number(userId) === Number(l.seller_id) && (
                            <button
                              className="om-btn ghost"
                              onClick={() => publishListing(l.id)}
                              disabled={publishLoadingId === l.id}
                              title="Publish (consume seller credit / admin bypass)"
                            >
                              {publishLoadingId === l.id ? "Processing..." : "Publish"}
                            </button>
                          )}
                        </>
                      )}

                      <button
                        className="om-btn"
                        onClick={() => {
                          if (typeof onEdit === "function") {
                            onEdit(l.id);
                          } else {
                            // fallback to previous route (keeps current behavior if parent didn't pass handler)
                            navigate(`/edit-listing/${l.id}`);
                          }
                        }}
                      >
                        Edit
                      </button>

                      {/* UPDATED: use handleView to record view then open listing */}
                      <button
                        className="om-btn ghost"
                        onClick={() => handleView(l)}
                        disabled={viewLoadingId === l.id}
                      >
                        {viewLoadingId === l.id ? "Opening..." : "View"}
                      </button>

                      {userId && Number(userId) === Number(l.seller_id) && String(l.status) !== "sold" && (
                        <button
                          className="om-btn ghost"
                          onClick={() => markAsSold(l)}
                          disabled={markSoldLoadingId === l.id}
                        >
                          {markSoldLoadingId === l.id ? "Processing..." : "Mark as Sold"}
                        </button>
                      )}

                      {userId && Number(userId) === Number(l.seller_id) && String(l.status) !== "sold" && (
                        <>
                          {(isExpired || isExpiringSoon) && (
                            <button
                              className="om-btn"
                              onClick={() => openRenewModal(l)}
                              disabled={renewLoadingId === l.id}
                            >
                              Renew
                            </button>
                          )}
                        </>
                      )}

                      <button
                        className="om-btn ghost"
                        onClick={() => viewContact(l)}
                        disabled={contactLoadingId === l.id}
                      >
                        {contactLoadingId === l.id ? "Loading..." : "Contact"}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        )}

        {/* Buyer area */}
        {!roleIsSeller && (
          <section className="om-listings" style={{ marginTop: 18 }}>
            <h2 className="om-section-title">My Activity</h2>
            <div style={{ color: "#666" }}>
              You can view your purchased plans in the Active Plan card above. Use the Contact button on any listing to view seller details if you have access.
            </div>
          </section>
        )}
      </main>
      {/* 🔥 ADD THE PLANS MODAL */}
      <SellerPlansModal
        open={plansOpen}
        onClose={() => {
          setPlansOpen(false);
          setSelectedMachineId(null);
        }}
        listingId={selectedMachineId}
        onPurchased={({ type, planId, paymentId, paymentFailed }) => {
          if (paymentFailed || !paymentId) {
            alert("Payment was cancelled or failed. Please retry.");
            return;
          }

          // Payment successful - show success message
          alert("Payment successful! Your listing will be published shortly.");
          setPlansOpen(false);
          setSelectedMachineId(null);

          // Refresh the page after a delay
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }}
      />

      {/* Renew Modal */}
      <RenewModal
        open={renewModalOpen}
        machineId={renewTargetId}
        onClose={() => { setRenewModalOpen(false); setRenewTargetId(null); }}
        onSuccess={(updated) => handleRenewSuccess(updated)}
      />
    </div>
  );
}
